import { DataTable } from '@/components/admin/members/data-table'
import { organizationsColumns } from '@/components/sudo/user/organizations-columns'
import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import {
  SudoContextCaseWorker,
  SudoContextOrganization,
  SudoContextUser,
} from '@/store/sudo'
import { CaseWorkerRole, CaseWorkerStatus } from '@repo/shared/dtos/caseWorker'
import { CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { Sheet, SheetContent, SheetHeader } from '@repo/ui/sheet'
import { RowSelectionState } from '@tanstack/react-table'
import { useMemo } from 'react'

export type Data = {
  firstName?: string | undefined
  id: string // organizationId
  lastName?: string | undefined
  organizationName: string
  role?: CaseWorkerRole | undefined
  caseWorkerId?: string | undefined
  status?: CaseWorkerStatus | undefined
}

export function OrganizationsEditor({
  caseWorkers,
  modalHandler,
  organizations,
  user,
}: {
  caseWorkers: SudoContextCaseWorker[]
  modalHandler: { open: boolean; setOpen: (v: boolean) => void }
  organizations: SudoContextOrganization[]
  user: SudoContextUser
}) {
  const dispatch = useStoreDispatch()
  const rowSelection = useMemo(
    () =>
      organizations.reduce((acc: RowSelectionState, v) => {
        acc[v.id] = user.organizationIds.includes(v.id)
        return acc
      }, {}),
    [organizations, user],
  )

  const data: Data[] = useMemo(
    () =>
      organizations.map((v) => {
        const caseWorker = caseWorkers.filter(
          (cw) => cw.organizationId === v.id,
        )[0]
        return {
          organizationName: v.name,
          caseWorkerId: caseWorker?.id,
          ...caseWorker,
          id: v.id,
        } satisfies Data
      }),
    [organizations, caseWorkers],
  )

  const updateCaseWorkerRole = ({
    caseWorkerId,
    organizationId,
    role,
  }: {
    caseWorkerId: string
    organizationId: string
    role: CaseWorkerRole
  }) => {
    void dispatch(
      sudoStoreActions.caseWorker.updateRole({
        role,
        caseWorkerId,
        organizationId,
      }),
    )
  }

  const joinOrLeaveOrganization = (
    action: 'join' | 'leave',
    organizationId: string,
  ) => {
    const organizationIds =
      action === 'join'
        ? [...user.organizationIds, organizationId] // add organizationId
        : user.organizationIds.filter((id) => id !== organizationId) // remove organizationId
    void dispatch(
      sudoStoreActions.user.updateOrganizationsAccess({
        organizationIds,
        userId: user.id,
      }),
    )
  }

  return (
    <Sheet open={modalHandler.open} onOpenChange={modalHandler.setOpen}>
      <SheetContent side="bottom" className="max-h-screen">
        <SheetHeader>
          <CardHeader className="px-0">
            <CardTitle>
              List of organizations {user.firstName} {user.lastName} has access
              to
            </CardTitle>
            <CardDescription>
              <span className="font-medium">{user.email}</span>
            </CardDescription>
          </CardHeader>
        </SheetHeader>
        <div className="max-h-screen overflow-hidden overflow-y-auto pb-40">
          <DataTable
            columns={organizationsColumns(
              joinOrLeaveOrganization,
              updateCaseWorkerRole,
            )}
            data={data}
            initialState={{
              rowSelection,
              sorting: [
                {
                  id: 'organizationName',
                  desc: false,
                },
              ],
            }}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}
